import { COUNTRIES, GENDER_OPTIONS, BLOOD_TYPES, getDocumentTypes } from "@alascorporation/utils"
import { parseCsv } from "./csv"
import type { BulkCreateRow } from "./api/studentsAdminClient"

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Plantilla esperada del CSV — encabezados tal como los exporta el formulario de inscripción. */
export const CSV_TEMPLATE_HEADERS = [
  "Nombres",
  "Apellidos",
  "Tipo de documento",
  "Numero de documento",
  "Fecha de nacimiento (Dia/Mes/Año)",
  "Género",
  "Correo electrónico",
  "Celular",
  "Nombre Completo (Acudiente)",
  "Parentesco",
  "Tipo de documento (Acudiente)",
  "Numero de documento (Acudiente)",
  "Numero de celular (Acudiente)",
  "Correo electrónico del acudiente",
  "Grupo sanguíneo",
  "Tiene alguna discapacidad",
  "Cuál es su discapacidad?",
  "Si toma algún medicamento, escríbalo a continuación",
  "Dirección residencia",
  "País",
  "Departamento / Estado / Provincia (Unificado)",
  "Municipio / Ciudad (Unificado)",
  "Institución educativa",
  "Rol en el equipo",
  "Nivel de formación académica en curso o último completado",
]

type FieldKey =
  | "firstName"
  | "lastName"
  | "documentType"
  | "documentNumber"
  | "birthDate"
  | "gender"
  | "email"
  | "phone"
  | "guardianName"
  | "guardianRelationship"
  | "guardianDocumentType"
  | "guardianDocumentNumber"
  | "guardianPhone"
  | "guardianEmail"
  | "bloodType"
  | "hasDisability"
  | "disabilityDetail"
  | "medications"
  | "address"
  | "country"
  | "stateProvince"
  | "city"
  | "institution"
  | "selfDeclaredRole"
  | "academicLevel"

function normalize(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase()
}

// Orden importante: los matchers de "acudiente" van antes que sus equivalentes
// generales, porque "Tipo de documento (Acudiente)" también contiene "tipo de documento".
const FIELD_MATCHERS: { key: FieldKey; test: (h: string) => boolean }[] = [
  { key: "guardianName", test: (h) => h.includes("acudiente") && h.includes("nombre") },
  { key: "guardianDocumentType", test: (h) => h.includes("acudiente") && h.includes("tipo de documento") },
  { key: "guardianDocumentNumber", test: (h) => h.includes("acudiente") && h.includes("numero de documento") },
  { key: "guardianPhone", test: (h) => h.includes("acudiente") && h.includes("celular") },
  { key: "guardianEmail", test: (h) => h.includes("acudiente") && h.includes("correo") },
  { key: "guardianRelationship", test: (h) => h.includes("parentesco") },
  { key: "firstName", test: (h) => h === "nombres" || (h.startsWith("nombre") && !h.includes("completo") && !h.includes("institucion")) },
  { key: "lastName", test: (h) => h.startsWith("apellido") },
  { key: "documentType", test: (h) => h.includes("tipo de documento") },
  { key: "documentNumber", test: (h) => h.includes("numero de documento") },
  { key: "birthDate", test: (h) => h.includes("fecha de nacimiento") },
  { key: "gender", test: (h) => h.includes("genero") },
  { key: "email", test: (h) => h.includes("correo") },
  { key: "phone", test: (h) => h.includes("celular") },
  { key: "bloodType", test: (h) => h.includes("grupo sanguineo") },
  { key: "hasDisability", test: (h) => h.includes("tiene alguna discapacidad") },
  { key: "disabilityDetail", test: (h) => h.includes("cual es su discapacidad") },
  { key: "medications", test: (h) => h.includes("medicamento") },
  { key: "address", test: (h) => h.includes("direccion") },
  { key: "stateProvince", test: (h) => h.includes("departamento") || h.includes("provincia") },
  { key: "city", test: (h) => h.includes("municipio") || h.includes("ciudad") },
  { key: "country", test: (h) => h === "pais" },
  { key: "institution", test: (h) => h.includes("institucion educativa") },
  { key: "selfDeclaredRole", test: (h) => h.includes("rol en el equipo") },
  { key: "academicLevel", test: (h) => h.includes("nivel de formacion") },
]

function truncate(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value
}

function resolveCountryCode(rawValue: string): string | undefined {
  if (!rawValue) return undefined
  const target = normalize(rawValue)
  const match = COUNTRIES.find((c) => normalize(c.label) === target || c.value.toLowerCase() === target)
  return match?.value
}

function resolveDocumentType(rawValue: string, countryCode: string | undefined): string {
  const raw = rawValue.trim()
  if (!raw) return raw
  const target = normalize(raw)
  const options = getDocumentTypes(countryCode)
  const match = options.find((o) => normalize(o.label) === target || o.value.toLowerCase() === target)
  return match ? match.value : truncate(raw, 20)
}

function resolveGender(rawValue: string): "M" | "F" | "NB" | "ND" | undefined {
  const raw = rawValue.trim()
  if (!raw) return undefined
  const target = normalize(raw)
  if (["m", "f", "nb", "nd"].includes(target)) return target.toUpperCase() as "M" | "F" | "NB" | "ND"
  const match = GENDER_OPTIONS.find((o) => normalize(o.label) === target)
  return match?.value
}

function resolveBoolean(rawValue: string): boolean | undefined {
  const target = normalize(rawValue)
  if (["si", "s", "yes", "true", "x", "1"].includes(target)) return true
  if (["no", "n", "false", "0", ""].includes(target)) return false
  return undefined
}

function resolveBirthDate(rawValue: string): string | undefined {
  const raw = rawValue.trim()
  const match = raw.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/)
  if (!match) return undefined
  const day = Number(match[1])
  const month = Number(match[2])
  const year = Number(match[3])
  if (month < 1 || month > 12 || day < 1 || day > 31) return undefined
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
}

export type ParsedStudentRow = {
  line: number
  displayName: string
  email: string
  error?: string
  warnings: string[]
  row: BulkCreateRow
}

function detectColumns(header: string[]): Partial<Record<FieldKey, number>> {
  const columns: Partial<Record<FieldKey, number>> = {}
  const usedKeys = new Set<FieldKey>()
  header.forEach((headerCell, index) => {
    const normalized = normalize(headerCell)
    const matcher = FIELD_MATCHERS.find((m) => !usedKeys.has(m.key) && m.test(normalized))
    if (matcher) {
      columns[matcher.key] = index
      usedKeys.add(matcher.key)
    }
  })
  return columns
}

function cell(cells: string[], columns: Partial<Record<FieldKey, number>>, key: FieldKey): string {
  const index = columns[key]
  return index === undefined ? "" : (cells[index] ?? "").trim()
}

export function parseStudentsCsv(text: string): { rows: ParsedStudentRow[]; error?: string } {
  const table = parseCsv(text)
  if (table.length === 0) {
    return { rows: [], error: "El archivo está vacío." }
  }

  const columns = detectColumns(table[0])
  if (columns.firstName === undefined || columns.lastName === undefined || columns.email === undefined) {
    return {
      rows: [],
      error:
        'No se encontraron las columnas "Nombres", "Apellidos" y "Correo electrónico" en la primera fila del CSV. Verifica que uses la plantilla esperada.',
    }
  }

  const rows: ParsedStudentRow[] = table.slice(1).map((cells, i) => {
    const line = i + 2
    const firstName = cell(cells, columns, "firstName")
    const lastName = cell(cells, columns, "lastName")
    const email = cell(cells, columns, "email")
    const displayName = [firstName, lastName].filter(Boolean).join(" ")
    const warnings: string[] = []

    if (!firstName || !lastName) {
      return { line, displayName, email, error: "Falta nombre o apellido", warnings, row: null as never }
    }
    if (!email || !EMAIL_RE.test(email)) {
      return { line, displayName, email, error: "Email inválido", warnings, row: null as never }
    }

    const countryRaw = cell(cells, columns, "country")
    const countryCode = resolveCountryCode(countryRaw)
    if (countryRaw && !countryCode) warnings.push(`País no reconocido: "${countryRaw}"`)

    const birthDateRaw = cell(cells, columns, "birthDate")
    const birthDate = birthDateRaw ? resolveBirthDate(birthDateRaw) : undefined
    if (birthDateRaw && !birthDate) warnings.push(`Fecha de nacimiento inválida: "${birthDateRaw}" (usa Día/Mes/Año)`)

    const genderRaw = cell(cells, columns, "gender")
    const gender = genderRaw ? resolveGender(genderRaw) : undefined
    if (genderRaw && !gender) warnings.push(`Género no reconocido: "${genderRaw}"`)

    const disabilityRaw = cell(cells, columns, "hasDisability")
    const hasDisability = disabilityRaw ? resolveBoolean(disabilityRaw) : undefined
    if (disabilityRaw && hasDisability === undefined) warnings.push(`"¿Tiene discapacidad?" no reconocido: "${disabilityRaw}" (usa Sí/No)`)

    const documentTypeRaw = cell(cells, columns, "documentType")
    const guardianDocumentTypeRaw = cell(cells, columns, "guardianDocumentType")

    const row: BulkCreateRow = {
      name: displayName,
      email,
      phone: cell(cells, columns, "phone") || undefined,
      country: countryCode,
      profile: {
        firstName,
        lastName,
        documentType: documentTypeRaw ? resolveDocumentType(documentTypeRaw, countryCode) : undefined,
        documentNumber: truncate(cell(cells, columns, "documentNumber"), 30) || undefined,
        birthDate,
        gender,
        countryCode,
        residenceCountryCode: countryCode,
        stateProvince: truncate(cell(cells, columns, "stateProvince"), 120) || undefined,
        city: truncate(cell(cells, columns, "city"), 120) || undefined,
        address: truncate(cell(cells, columns, "address"), 255) || undefined,
        guardianName: truncate(cell(cells, columns, "guardianName"), 255) || undefined,
        guardianRelationship: truncate(cell(cells, columns, "guardianRelationship"), 80) || undefined,
        guardianDocumentType: guardianDocumentTypeRaw ? resolveDocumentType(guardianDocumentTypeRaw, countryCode) : undefined,
        guardianDocumentNumber: truncate(cell(cells, columns, "guardianDocumentNumber"), 30) || undefined,
        guardianPhone: truncate(cell(cells, columns, "guardianPhone"), 30) || undefined,
        guardianEmail: cell(cells, columns, "guardianEmail") || undefined,
        bloodType: (() => {
          const raw = cell(cells, columns, "bloodType").toUpperCase()
          if (!raw) return undefined
          if (!BLOOD_TYPES.includes(raw)) warnings.push(`Grupo sanguíneo no reconocido: "${raw}"`)
          return truncate(raw, 5)
        })(),
        hasDisability,
        disabilityDetail: truncate(cell(cells, columns, "disabilityDetail"), 500) || undefined,
        medications: truncate(cell(cells, columns, "medications"), 500) || undefined,
        institution: truncate(cell(cells, columns, "institution"), 255) || undefined,
        selfDeclaredRole: truncate(cell(cells, columns, "selfDeclaredRole"), 120) || undefined,
        academicLevel: truncate(cell(cells, columns, "academicLevel"), 80) || undefined,
      },
    }

    return { line, displayName, email, warnings, row }
  })

  return { rows }
}
